import { NumericValue } from "@edgetier/client-components";
import { FormattedDateTime } from "@edgetier/client-components";
import { FunctionComponent, memo, useMemo } from "react";

import { DECIMAL_PLACES } from "./metric-value-chart-tooltip.constants";
import { IProps } from "./metric-values-chart-tooltip.types";
import "./metric-values-chart-tooltip.scss";

/**
 * Show a tooltip of the predicted vs actual value.
 * @param props.actualValue    Real value.
 * @param props.dateTime       When the value was captured.
 * @param props.predictedLower The lower value of the confidence range.
 * @param props.predictedUpper The upper value of the confidence range.
 * @returns                    Details of metric model output.
 */
const MetricValuesChartTooltip: FunctionComponent<IProps> = ({
    actualValue,
    dateTime,
    predictedLower,
    predictedUpper,
}) => {
    const differenceFromExpected = useMemo(() => {
        if (actualValue >= predictedLower && actualValue <= predictedUpper) {
            return 0;
        }

        if (actualValue < predictedLower) {
            return actualValue - predictedLower;
        }

        return actualValue - predictedUpper;
    }, [actualValue, predictedUpper, predictedLower]);

    return (
        <div className="metric-values-chart-tooltip">
            <FormattedDateTime dateTime={dateTime} />

            <div className="metric-values-chart-tooltip__delta">
                {differenceFromExpected === 0 ? (
                    <>In Range</>
                ) : (
                    <>
                        {differenceFromExpected > 0 && <>+</>}
                        <NumericValue
                            options={{ maximumFractionDigits: DECIMAL_PLACES }}
                            value={differenceFromExpected}
                        />
                    </>
                )}
            </div>

            <div className="metric-values-chart-tooltip__measures">
                <div className="metric-values-chart-tooltip__measure">
                    Expected
                    <div className="metric-values-chart-tooltip__measure-value">
                        <NumericValue options={{ maximumFractionDigits: DECIMAL_PLACES }} value={predictedLower} />{" "}
                        &ndash;{" "}
                        <NumericValue options={{ maximumFractionDigits: DECIMAL_PLACES }} value={predictedUpper} />
                    </div>
                </div>

                <div className="metric-values-chart-tooltip__measure">
                    Actual
                    <div className="metric-values-chart-tooltip__measure-value">
                        <NumericValue options={{ maximumFractionDigits: DECIMAL_PLACES }} value={actualValue} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(MetricValuesChartTooltip);
